import { createDebugLogger } from "@utils/debugLog";

const log = createDebugLogger("embed:host");

/**
 * Hosts this app knows how to run inside. "mda" is a Microsoft Dataverse
 * model-driven app, where the app is framed by an HTML web resource. "teams"
 * is a Microsoft Teams tab, where the app is framed by the Teams client and
 * the manifest's contentUrl carries the parameter.
 */
const EMBED_HOSTS = ["mda", "teams"] as const;

export type EmbedHost = (typeof EMBED_HOSTS)[number];

/** Query parameter the host's loader script puts on the iframe URL. */
export const EMBED_HOST_QUERY_PARAM = "embed";

/**
 * Where the answer is kept once the query string is gone. The loader only puts
 * the parameter on the *initial* iframe URL and the router drops it on the
 * first navigation, so detection has to outlive it. sessionStorage is scoped to
 * this frame, so a normal tab on the same origin never sees this value.
 */
export const EMBED_HOST_STORAGE_KEY = "otl.embedHost";

const isEmbedHost = (value: string | null): value is EmbedHost =>
  EMBED_HOSTS.includes(value as EmbedHost);

const readStoredHost = (): EmbedHost | null => {
  try {
    const stored = window.sessionStorage.getItem(EMBED_HOST_STORAGE_KEY);
    return isEmbedHost(stored) ? stored : null;
  } catch {
    // Private browsing, or site data blocked. Detection still works for as long
    // as the parameter is on the URL, which covers the first render.
    return null;
  }
};

const storeHost = (host: EmbedHost): void => {
  try {
    window.sessionStorage.setItem(EMBED_HOST_STORAGE_KEY, host);
  } catch {
    // See readStoredHost — nothing useful to do, and nothing worth failing over.
  }
};

const detectEmbedHost = (): EmbedHost | null => {
  const fromQuery = new URLSearchParams(window.location.search).get(
    EMBED_HOST_QUERY_PARAM,
  );

  if (isEmbedHost(fromQuery)) {
    storeHost(fromQuery);
    return fromQuery;
  }

  return readStoredHost();
};

let resolvedHost: EmbedHost | null | undefined;

/**
 * The host this app was launched into, or null when it is running as an
 * ordinary standalone web app. Resolved once and remembered, because callers
 * include a module-level singleton (the OIDC client) as well as React.
 *
 * Deliberately says nothing about whether a parent window is reachable — that
 * is the bridge's question, and the two differ when an embed URL is opened
 * top-level for debugging.
 */
export const getEmbedHost = (): EmbedHost | null => {
  if (resolvedHost === undefined) {
    resolvedHost = detectEmbedHost();
    log("resolved embed host", { host: resolvedHost });
  }

  return resolvedHost;
};

/** True when the app is running inside a host rather than on its own. */
export const isEmbedded = (): boolean => getEmbedHost() !== null;

/**
 * True when the host is a Teams tab. Teams needs its own sign-in strategy —
 * the Teams client mediates the popup rather than the app opening one — so
 * callers that branch on it ask for the host, not just whether it is embedded.
 */
export const isTeamsEmbed = (): boolean => getEmbedHost() === "teams";

/**
 * Forgets the remembered answer so the next call re-reads the environment.
 * Only for tests — nothing in the app changes host mid-session.
 */
export const resetEmbedHostCache = (): void => {
  resolvedHost = undefined;
};
